

import { useState, useRef, useEffect } from "react";
import { Calendar as CalendarIcon, X } from "lucide-react";
import { formatDate } from "@/lib/dateFormat";

interface DateRangePickerProps {
  fromDate: string;
  toDate: string;
  onFromChange: (date: string) => void;
  onToChange: (date: string) => void;
  placeholder?: string;
}

export default function DateRangePicker({
  fromDate,
  toDate,
  onFromChange,
  onToChange,
  placeholder = "Select date range"
}: DateRangePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selecting, setSelecting] = useState<"from" | "to">("from");
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getDisplayText = () => {
    if (fromDate && toDate) {
      return `${formatDisplayDate(fromDate)} - ${formatDisplayDate(toDate)}`;
    }
    if (fromDate) {
      return `${formatDisplayDate(fromDate)} - ...`;
    }
    return placeholder;
  };

  const formatDisplayDate = (dateStr: string) => {
    if (!dateStr) return "";
    return formatDate(dateStr);
  };

  const getDaysInMonth = (year: number, month: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year: number, month: number) => {
    return new Date(year, month, 1).getDay();
  };

  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

  const handleDateClick = (day: number) => {
    const selectedDate = `${currentYear}-${String(currentMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    
    if (selecting === "from") {
      onFromChange(selectedDate);
      onToChange(""); // Reset to date when from changes
      setSelecting("to");
    } else {
      // Ensure to date is not before from date
      if (fromDate && new Date(selectedDate) < new Date(fromDate)) {
        onFromChange(selectedDate);
        onToChange(fromDate);
      } else {
        onToChange(selectedDate);
      }
      setSelecting("from");
      setIsOpen(false);
    }
  };

  const isDateInRange = (day: number) => {
    if (!fromDate || !toDate) return false;
    const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    const date = new Date(dateStr);
    const from = new Date(fromDate);
    const to = new Date(toDate);
    return date > from && date < to;
  };

  const isDateSelected = (day: number, type: "from" | "to") => {
    const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    if (type === "from") return dateStr === fromDate;
    return dateStr === toDate;
  };

  const clearSelection = () => {
    onFromChange("");
    onToChange("");
    setSelecting("from");
  };

  const days = getDaysInMonth(currentYear, currentMonth);
  const firstDay = getFirstDayOfMonth(currentYear, currentMonth);
  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-3 py-2 rounded-xl border border-stone-200 text-sm bg-white hover:border-amber-400 focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none transition-colors"
      >
        <div className="flex items-center gap-2">
          <CalendarIcon className="w-4 h-4 text-stone-400" />
          <span className={fromDate || toDate ? "text-stone-800" : "text-stone-400"}>
            {getDisplayText()}
          </span>
        </div>
        {(fromDate || toDate) && (
          <div
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              clearSelection();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
                clearSelection();
              }
            }}
            className="p-1 rounded hover:bg-stone-100 cursor-pointer"
          >
            <X className="w-3 h-3 text-stone-400" />
          </div>
        )}
      </button>

      {/* Calendar Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-2 z-50 bg-white rounded-xl border border-stone-200 shadow-lg p-4 w-[320px]">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <button
              type="button"
              onClick={() => {
                if (currentMonth === 0) {
                  setCurrentMonth(11);
                  setCurrentYear(currentYear - 1);
                } else {
                  setCurrentMonth(currentMonth - 1);
                }
              }}
              className="p-1 rounded hover:bg-stone-100"
            >
              <svg className="w-5 h-5 text-stone-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <span className="font-medium text-stone-800">
              {monthNames[currentMonth]} {currentYear}
            </span>
            <button
              type="button"
              onClick={() => {
                if (currentMonth === 11) {
                  setCurrentMonth(0);
                  setCurrentYear(currentYear + 1);
                } else {
                  setCurrentMonth(currentMonth + 1);
                }
              }}
              className="p-1 rounded hover:bg-stone-100"
            >
              <svg className="w-5 h-5 text-stone-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Selection Mode Indicator */}
          <div className="flex gap-2 mb-3">
            <button
              type="button"
              onClick={() => setSelecting("from")}
              className={`flex-1 py-1.5 px-2 rounded-lg text-xs font-medium transition-colors ${
                selecting === "from" 
                  ? "bg-amber-100 text-amber-700 border border-amber-200" 
                  : "bg-stone-100 text-stone-600 border border-stone-200"
              }`}
            >
              From: {fromDate ? formatDisplayDate(fromDate) : "Select"}
            </button>
            <button
              type="button"
              onClick={() => setSelecting("to")}
              className={`flex-1 py-1.5 px-2 rounded-lg text-xs font-medium transition-colors ${
                selecting === "to" 
                  ? "bg-amber-100 text-amber-700 border border-amber-200" 
                  : "bg-stone-100 text-stone-600 border border-stone-200"
              }`}
            >
              To: {toDate ? formatDisplayDate(toDate) : "Select"}
            </button>
          </div>

          {/* Weekday Headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
              <div key={day} className="text-center text-xs font-medium text-stone-400 py-1">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: firstDay }).map((_, i) => (
              <div key={`empty-${i}`} className="h-8" />
            ))}
            {Array.from({ length: days }).map((_, i) => {
              const day = i + 1;
              const isFrom = isDateSelected(day, "from");
              const isTo = isDateSelected(day, "to");
              const inRange = isDateInRange(day);
              
              return (
                <button
                  key={day}
                  type="button"
                  onClick={() => handleDateClick(day)}
                  className={`h-8 w-8 rounded-lg text-sm font-medium transition-colors ${
                    isFrom || isTo
                      ? "bg-amber-500 text-white"
                      : inRange
                      ? "bg-amber-100 text-amber-700"
                      : "hover:bg-stone-100 text-stone-700"
                  }`}
                >
                  {day}
                </button>
              );
            })}
          </div>

          {/* Quick Select Buttons */}
          <div className="flex gap-2 mt-4 pt-3 border-t border-stone-100">
            <button
              type="button"
              onClick={() => {
                const today = new Date();
                const lastWeek = new Date(today);
                lastWeek.setDate(today.getDate() - 7);
                onFromChange(lastWeek.toISOString().slice(0, 10));
                onToChange(today.toISOString().slice(0, 10));
                setIsOpen(false);
              }}
              className="flex-1 py-1.5 px-2 rounded-lg text-xs font-medium bg-stone-100 text-stone-600 hover:bg-stone-200 transition-colors"
            >
              Last 7 days
            </button>
            <button
              type="button"
              onClick={() => {
                const today = new Date();
                const lastMonth = new Date(today);
                lastMonth.setMonth(today.getMonth() - 1);
                onFromChange(lastMonth.toISOString().slice(0, 10));
                onToChange(today.toISOString().slice(0, 10));
                setIsOpen(false);
              }}
              className="flex-1 py-1.5 px-2 rounded-lg text-xs font-medium bg-stone-100 text-stone-600 hover:bg-stone-200 transition-colors"
            >
              Last 30 days
            </button>
            <button
              type="button"
              onClick={() => {
                const today = new Date();
                onFromChange(today.toISOString().slice(0, 10));
                onToChange(today.toISOString().slice(0, 10));
                setIsOpen(false);
              }}
              className="flex-1 py-1.5 px-2 rounded-lg text-xs font-medium bg-stone-100 text-stone-600 hover:bg-stone-200 transition-colors"
            >
              Today
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
